import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { KpiTypes as pestKpiTypes, ServiceMetric, FindingMetric, CallMetric, SICallTypes, ServiceMetricTypes, SICallTypesColorCode, SICallTypesDisplayName, SITotalCallsByType, PestTotalCallsByTypeData } from '../../types/service-insight-types';
import { ServiceInsightsService } from '../../services/service-insights-service.service';
import { SpreadsheetService } from 'src/app/core/services/spreadsheet.service';
import { ServiceCallDistributionItem } from '../service-call-distribution-chart/service-call-distribution-chart.component';
import { ServiceCallDistributionChartConfigResolver } from '../../resolvers/service-call-distribution-chart-config.resolver';

@Component({
    selector: 'app-pest-service-insights',
    templateUrl: './pest-service-insights.component.html',
    styleUrls: ['./pest-service-insights.component.scss', '../service-insights-kpi-bar/service-insights-kpi-bar.component.scss'],
    standalone: false
})
export class PestServiceInsightsComponent implements OnInit, OnDestroy {
  allChecked: boolean = true;
  filters: ContractModel | undefined;
  loading: boolean = false;
 
  metrics: ServiceMetric[] = [];
  findingMetrics: FindingMetric[] =[];
  callMetrics: CallMetric[] = [];

  // One stacked-bar distribution dataset per Pest service metric type.
  serviceDistributionItems: ServiceCallDistributionItem[] = [];
  findingDistributionItems: ServiceCallDistributionItem[] = [];
  callDistributionItems: ServiceCallDistributionItem[] = [];

  //expose enums for html
  ServiceMetricTypes = ServiceMetricTypes;
  pestKpiTypes = pestKpiTypes;

  checkStatusSubscription: Subscription | undefined;
  private distributionSubscriptions: Subscription[] = [];

  downloadSpreadsheetStatus: string = '';
  spreadsheetDownloadStatus: Subscription | undefined;

  constructor(private _filterService: FiltersService,
    private _serviceInsightsService: ServiceInsightsService,
    private _spreadsheetService: SpreadsheetService,
    private _distributionConfigResolver: ServiceCallDistributionChartConfigResolver,
  ) {
  }

  async ngOnInit() {
    // Get the filters selected
    this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);
    this.spreadsheetDownloadStatus = this._spreadsheetService.downloadStatus$.subscribe(status => {
      this.downloadSpreadsheetStatus = status;
    });
    this.setMetrics();
    this.subscribeToPestMetrics();
    this.subscribeToDistributionData();
    await this.loadDistributionData();
  }

  /**
   * Fetches the by-type "total calls" payload for each Pest metric type and pushes it into the
   * corresponding store. The store emissions flow back through subscribeToDistributionData() to rebuild the chart items.
   */
  async loadDistributionData() {
    if (this.filters === undefined) {
      return;
    }

    this.loading = true;
    // Pest has no advanced (service customer) filters; the service seeds an empty set.
    const advancedFilters = this._serviceInsightsService.serviceCustomerFilters.getValue();

    const requests: Array<{ key: keyof PestTotalCallsByTypeData; serviceMetricType: ServiceMetricTypes }> = [
      { key: 'Services-total-calls',      serviceMetricType: ServiceMetricTypes.Services },
      { key: 'Findings-total-calls',      serviceMetricType: ServiceMetricTypes.Findings },
      { key: 'CallsByReason-total-calls', serviceMetricType: ServiceMetricTypes.CallsByReason },
    ];

    await Promise.all(
      requests.map(async request => {
        const data = await this._serviceInsightsService.getTotalCallsByTypeFromAPI(
          request.serviceMetricType,
          advancedFilters,
        );
        if (data) {
          this._serviceInsightsService.setPestTotalCallsByTypeData(request.key, data);
        }
      }),
    );

    this.loading = false;
  }

  subscribeToPestMetrics(){
    this.checkStatusSubscription = this._serviceInsightsService?.isPestMetricsSelected?.subscribe((metrics:Map<string,boolean>)=>{
      this.allChecked = !Array.from(metrics.values()).some(value => value === false);
    });
  }

  /**
   * Subscribes to each Pest by-type "total calls" store and rebuilds the stacked-bar
   * distribution items whenever the underlying data changes — one dataset per metric type
   * (Services, Findings, Calls). Mirrors ServiceInsightsComponent's single non-Pest chart.
   */
  subscribeToDistributionData() {
    const sources: Array<{
      key: keyof PestTotalCallsByTypeData;
      serviceMetricType: ServiceMetricTypes;
      assign: (items: ServiceCallDistributionItem[]) => void;
    }> = [
      { key: 'Services-total-calls',     serviceMetricType: ServiceMetricTypes.Services,     assign: items => (this.serviceDistributionItems = items) },
      { key: 'Findings-total-calls',     serviceMetricType: ServiceMetricTypes.Findings,     assign: items => (this.findingDistributionItems = items) },
      { key: 'CallsByReason-total-calls', serviceMetricType: ServiceMetricTypes.CallsByReason, assign: items => (this.callDistributionItems = items) },
    ];

    for (const source of sources) {
      const subscription = this._serviceInsightsService
        .getPestTotalCallsByTypeData(source.key)
        .subscribe(data => source.assign(this._buildDistributionItems(source.serviceMetricType, data)));
      this.distributionSubscriptions.push(subscription);
    }
  }

  /**
   * Maps the raw by-type API payload (keyed by the `callReason` string) onto the
   * ServiceCallDistributionChart's item shape for a given Pest metric type.
   *
   * The category config (order/label/color) is owned by the resolver, whose `key` is the
   * raw API `callReason`, so incoming totals merge straight onto it. Reasons not present in
   * the config are dropped; missing categories are defaulted to 0 by the chart itself.
   */
  private _buildDistributionItems(
    serviceMetricType: ServiceMetricTypes,
    data: SITotalCallsByType,
  ): ServiceCallDistributionItem[] {
    const configByReason = new Map(
      this._distributionConfigResolver
        .resolve(serviceMetricType)
        .categories
        .map(config => [config.key, config]),
    );

    return data.totalCalls
      .filter(item => configByReason.has(item.callReason))
      .map(({ callReason, total, totalLocations }) => {
        const config = configByReason.get(callReason)!;
        return {
          key: config.key,
          label: config.label,
          value: total,
          totalLocations,
          color: config.color,
        };
      });
  }

  setMetrics() {
    const keys: SICallTypes[] = [SICallTypes.ContractedCleanOut, SICallTypes.ContractedRegular, SICallTypes.ExtraOneShot, SICallTypes.ExtraFollowUp];
    const findingsKeys: SICallTypes[] = [SICallTypes.Pest, SICallTypes.Sanitation, SICallTypes.Structural, SICallTypes.Prep];
    const callKeys: SICallTypes[] = [SICallTypes.ExtraServiceRequest]; // Can be extended to include other call types

    this.metrics = keys.map(key => ({
      key,
      title:  SICallTypesDisplayName[key],
      borderColor: SICallTypesColorCode[key]
    }))

    this.findingMetrics = findingsKeys.map(key => ({
      key,
      title: SICallTypesDisplayName[key],
      borderColor: SICallTypesColorCode[key]
    }))

    this.callMetrics = callKeys.map(key => ({
      key,
      title: SICallTypesDisplayName[key],
      borderColor: SICallTypesColorCode[key]
    }))
  }

  _onAllCheckedChange(checked: boolean) {
    this.allChecked = checked;
    // If "select all" is checked, we want to select all metrics including Pest Catches. 
    // If it's unchecked, we want to unselect all metrics including Pest Catches.
    this._serviceInsightsService.updateIsPestCatchesSelected(checked);
    this._serviceInsightsService?.affectAllMetricsToPresentation(this.allChecked);
  }

  ngOnDestroy() {
    this.checkStatusSubscription?.unsubscribe();
    this.spreadsheetDownloadStatus?.unsubscribe();
    this.distributionSubscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
